import { hashObject, stableStringify } from '@fullstory/util/hash';
import { QueryKey } from '@tanstack/react-query';
import { CacheKey, CacheKeyFunction, GRPCRequestConfig, RequestConfig } from './types';

export const defaultCacheKey: CacheKeyFunction = (path, body, params): CacheKey =>
  `${path}${body === undefined ? '' : `:${hashObject(body)}`}${
    params === undefined ? '' : `:${hashObject(params)}`
  }`;

/**
 * Constructs a cache key for a given GRPCRequestConfig.
 *
 * To be used as the queryKey and mutationKey in react-query.
 */
export const grpcRequestConfigCacheKey = <TBody, TResponse>(
  requestConfig: GRPCRequestConfig<TBody, TResponse>,
): CacheKey => {
  const endpoint = requestConfig.request.grpcFunction(Promise.resolve, {}).endpoint;
  const cacheKeyFn = requestConfig.cacheKey ?? defaultCacheKey;
  return cacheKeyFn(endpoint, requestConfig.request.body);
};

/**
 * Constructs a cache key for a given RequestConfig.
 *
 * To be used as the queryKey and mutationKey in react-query.
 */
export const requestConfigCacheKey = <B, R>(requestConfig: RequestConfig<B, R>): CacheKey => {
  const endpoint = requestConfig.request.buildRequest.url;
  const cacheKeyFn = requestConfig.cacheKey ?? defaultCacheKey;
  return cacheKeyFn(
    endpoint,
    requestConfig.request.body,
    requestConfig.request.buildRequest.params,
  );
};

/**
 * Converts a {@link CacheKey} to a {@link QueryKey}.
 */
export const cacheKeyToQueryKey = (cacheKey: CacheKey): ReadonlyArray<string> => [cacheKey];

/**
 * A stable stringify function that returns undefined if an error occurs.
 */
export const stringifyQueryKey = (queryKey: QueryKey): string | undefined => {
  try {
    const coalesced = queryKey.map(part => {
      switch (part) {
        case undefined:
          return 'undefined';
        case null:
          return 'null';
        default:
          return part;
      }
    });
    return stableStringify(coalesced);
  } catch {
    return undefined;
  }
};
